import { Box, Paper } from '@mui/material';
import type { ReactNode } from 'react';
import { designTokens } from '../tokens';
import { PageHeader, type PageContentWidth, type PageHeaderProps } from './PageHeader';
import { PageBody } from './PageBody';
import { pageGutter } from './pageContent';

const pageBodyStartPadding = {
  xs: `${designTokens.layout.pageBodyStartGapCompact}px`,
  md: `${designTokens.layout.pageBodyStartGap}px`,
} as const;

export type PageScaffoldProps = Omit<PageHeaderProps, 'contentWidth' | 'prominent'> & {
  children: ReactNode;
  width?: PageContentWidth;
  prominent?: boolean;
  presentation?: 'plain' | 'sectioned' | 'work-surface';
  bodyMode?: 'flow' | 'fill' | 'scroll';
  footer?: ReactNode;
  testId?: string;
  template?: 'resource' | 'information' | 'edit' | 'guide';
};

export function PageScaffold({
  children,
  width = 'standard',
  prominent = false,
  presentation = 'plain',
  bodyMode = 'flow',
  tabs,
  footer,
  testId,
  template,
  ...headerProps
}: PageScaffoldProps) {
  const fillsWorkspace = bodyMode !== 'flow';
  const workSurface = presentation === 'work-surface';

  const pageBodyContent = workSurface ? (
    fillsWorkspace ? (
      <Box sx={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <Box
          data-testid="page-body-start"
          sx={{ flex: 1, minHeight: 0, pt: pageBodyStartPadding, display: 'flex', flexDirection: 'column' }}
        >
          <Paper
            data-testid="page-work-surface"
            variant="outlined"
            sx={{
              flex: 1,
              minHeight: 0,
              p: pageGutter,
              boxSizing: 'border-box',
              overflow: bodyMode === 'scroll' ? 'auto' : 'hidden',
              borderRadius: designTokens.radius.surface,
              bgcolor: designTokens.color.canvas,
            }}
          >
            <Box sx={{ height: '100%', minHeight: 0 }}>{children}</Box>
          </Paper>
        </Box>
      </Box>
    ) : (
      <Box data-testid="page-body-start" sx={{ pt: pageBodyStartPadding }}>
        <Paper
          data-testid="page-work-surface"
          variant="outlined"
          sx={{ p: pageGutter, borderRadius: designTokens.radius.surface, bgcolor: designTokens.color.canvas }}
        >
          {children}
        </Paper>
      </Box>
    )
  ) : (
    // 결과 유무로 flow/fill이 바뀌어도 입력·탭의 DOM과 초점을 유지합니다.
    <Box sx={{
      height: fillsWorkspace ? '100%' : undefined,
      minHeight: 0,
      display: fillsWorkspace ? 'flex' : undefined,
      flexDirection: fillsWorkspace ? 'column' : undefined,
    }}>
      <Box
        data-testid="page-body-start"
        sx={{
          flex: fillsWorkspace ? 1 : undefined,
          minHeight: 0,
          overflow: bodyMode === 'scroll' ? 'auto' : undefined,
          pt: pageBodyStartPadding,
          display: bodyMode === 'fill' ? 'flex' : undefined,
          flexDirection: bodyMode === 'fill' ? 'column' : undefined,
        }}
      >
        <Box sx={{ flex: bodyMode === 'fill' ? 1 : undefined, minHeight: 0 }}>{children}</Box>
      </Box>
    </Box>
  );

  const pageContent = <>
    <PageHeader {...headerProps} tabs={tabs} contentWidth={width} prominent={prominent} />
    <PageBody width={width} testId={testId} fill={fillsWorkspace}>
      {pageBodyContent}
    </PageBody>
  </>;

  return (
    <Box
      data-testid="page-scaffold"
      data-page-presentation={presentation}
      data-page-template={template ?? 'custom'}
      sx={{
        width: '100%',
        minWidth: 0,
        minHeight: fillsWorkspace ? 0 : `calc(100vh - ${designTokens.layout.workspaceHeaderHeight}px)`,
        '@supports (height: 100dvh)': {
          minHeight: fillsWorkspace ? 0 : `calc(100dvh - ${designTokens.layout.workspaceHeaderHeight}px)`,
        },
        height: fillsWorkspace ? '100%' : undefined,
        display: fillsWorkspace ? 'flex' : 'block',
        flexDirection: fillsWorkspace ? 'column' : undefined,
        overflow: presentation === 'sectioned' && bodyMode === 'fill' ? 'auto' : undefined,
        bgcolor: designTokens.color.pageCanvas,
      }}
    >
      {pageContent}
      {footer}
    </Box>
  );
}
